import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { api } from '../../../lib/api';

// Portal /portal/settings/notifications — Sprint D item 3.8.
// Per-event toggle table for the calling client user.

const EVENT_LABELS = {
  edit_request_resolved:   'Edit request resolved',
  ticket_resolved:         'Ticket resolved',
  invoice_issued:          'Invoice issued',
  invoice_signed:          'Invoice signed (admin notification)',
  payment_received:        'Payment received',
  maintenance_renewal_due: 'Maintenance renewal due',
  audit_completed:         'Audit completed',
};

export default function PortalNotificationSettings() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/client/notification-preferences')
      .then((d) => { if (alive) setData(d); })
      .catch((err) => { if (alive) setError(err?.data?.error || err?.message || 'Failed to load'); });
    return () => { alive = false; };
  }, []);

  const onToggle = async (eventType, nextEnabled) => {
    setSaving(eventType);
    try {
      await api.patch('/api/client/notification-preferences', { preferences: { [eventType]: nextEnabled } });
      setData((prev) => prev ? { ...prev, preferences: { ...prev.preferences, [eventType]: nextEnabled } } : prev);
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div data-testid="portal-notifications-settings" className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#B0FC38]" /> Notification <span className="serif-acc accent-text">preferences</span>
        </h1>
        <p className="text-[13px] text-gray-400 mt-1">
          Toggle which transactional emails NexSav should send you. Changes apply immediately.
        </p>
      </div>

      {error && <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">{error}</div>}

      {!data && !error && <div className="text-[13px] text-gray-500">Loading…</div>}

      {data && (
        <ul className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04]">
          {data.events.map((e) => {
            const enabled = data.preferences[e] !== false;
            return (
              <li key={e} className="flex items-center justify-between px-4 py-3" data-testid={`pref-row-${e}`}>
                <div>
                  <div className="text-[13px] text-white">{EVENT_LABELS[e] || e}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{e}</div>
                </div>
                <label className="inline-flex items-center cursor-pointer gap-2 text-[12px] text-gray-300">
                  <input
                    type="checkbox"
                    data-testid={`pref-toggle-${e}`}
                    checked={enabled}
                    disabled={saving === e}
                    onChange={(ev) => onToggle(e, ev.target.checked)}
                    className="w-3.5 h-3.5 rounded accent-[#B0FC38]"
                  />
                  {enabled ? 'On' : 'Off'}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
